import React, { JSX } from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import EDButton, {
  IProps as IButtonProps,
} from '../../Buttons/Button/EDButton';
import classNames from 'classnames';
import shortid from 'shortid';

export interface IDialogFooterActionButton {
  /** The label of the button */
  label?: string | JSX.Element;
  /** The icon of the button */
  icon?: React.ReactNode;
  /** Click handler for the action button */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** These props will be forwarded to the actions button component */
  buttonProps?: Partial<IButtonProps>;
  testId?: string;
}

export interface IProps {
  actions?: IDialogFooterActionButton[];
  content?: React.ReactNode;
  footerResetResourceActions?: false | IDialogFooterActionButton[] | undefined;
  testId?: string;
  classes?: Partial<Record<keyof ReturnType<typeof styles>, string>>;
  className?: string;
}

const styles = (theme: Theme) => ({
  /** Styles applied to the root element */
  root: {
    background: theme.customProperties.Dialog.actionsBg,
    color: theme.customProperties.Dialog.actionsColor,
    padding: theme.spacing(0, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  /** Styles applied to the inner container element */
  innerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  /** Styles applied to the inner container element if content is undefined */
  noContent: {
    justifyContent: 'flex-end',
  },
  /** Styles applied to the actions wrapper element */
  actions: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    flexShrink: 0,
  },
  /** Styles applied to the content wrapper element */
  content: {},
  /** Styles applied to the action buttons */
  actionButton: {
    margin: theme.spacing(0, 1),
  },
});

class EDDialogFooter extends React.Component<IProps> {
  renderActions = (actions: IDialogFooterActionButton[], testId?: string) =>
    actions.map(action => {
      const buttonProps = action.buttonProps;
      const id: string | undefined =
        typeof action.label === 'string' ? action.label : undefined;
      const key: string = id ?? shortid.generate();
      return (
        <EDButton
          key={key}
          onClick={action.onClick}
          label={action.label}
          testId={testId ? `${testId}-${id}` : id}
          icon={action.icon}
          flat
          primary
          marginX={1}
          {...buttonProps}
        />
      );
    });

  renderResetResourceFooterActions = (
    footerResetResourceActions: IDialogFooterActionButton[]
  ) =>
    footerResetResourceActions.map(action => {
      let buttonProps = action.buttonProps;
      return (
        <EDButton
          key={shortid.generate()}
          onClick={action.onClick}
          label={action.label}
          icon={action.icon}
          flat
          primary
          marginX={1}
          {...buttonProps}
        />
      );
    });

  render() {
    const {
      actions,
      className,
      content,
      footerResetResourceActions: footerResetResourceActions,
    } = this.props;
    const classes = withStyles.getClasses(this.props);
    return (
      <div className={classNames(classes.root, className)}>
        {this.props.footerResetResourceActions ? (
          <div
            className={classNames(classes.innerContainer, {
              [classes.noContent]: content == null,
            })}
          >
            {content && <div className={classes.content}>{content}</div>}
            {actions && footerResetResourceActions && (
              <div className={classes.actions}>
                {this.renderResetResourceFooterActions(
                  footerResetResourceActions
                )}
                {this.renderActions(actions, this.props.testId)}
              </div>
            )}
          </div>
        ) : (
          <div
            className={classNames(classes.innerContainer, {
              [classes.noContent]: content == null,
            })}
          >
            {content && <div className={classes.content}>{content}</div>}
            {actions && (
              <div className={classes.actions}>
                {this.renderActions(actions, this.props.testId)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default withStyles(EDDialogFooter, styles);
